import { useState } from 'react'
import useFormProcessor from '@hooks/useFormProcessor'
import { ReportsProp } from '@templates/ReportsButton'
import {
  ANTI_FIRE_REPORT_NAME,
  ELECTRO_SAFE_REPORT_NAME,
  EMPLOYEES_EDUCATION_STATS_REPORT_NAME,
  INITIAL_BRIEFING_REPORT_NAME,
  OTHER_BRIEFING_REPORT_NAME,
  POSITIONS_REPORT_NAME,
  EMPLOYEES_EDUCATION_STATS_REPORT,
  INITIAL_BRIEFING_REPORT,
  OTHER_BRIEFING_REPORT,
  ANTI_FIRE_REPORT,
  ELECTRO_SAFE_REPORT,
  POSITIONS_REPORT,
} from '@const/reportNames'
import {
  useGetElectricalSafetyReportMutation,
  useGetEmployeesInitialBriefingReportMutation,
  useGetEmployeesOtherBriefingReportMutation,
  useGetFireSafetyReportMutation,
} from '@services/employeeEducation'
import { Option } from '@organisms/Select'
import { CompanyDetailed } from '@models/company'
import { DivisionDetailed } from '@models/division'
import { FilialDetailed } from '@models/filial'
import { PositionDetailed } from '@models/position'
import { User } from '@models/user'
import { useGetEmployeesReportMutation } from '@services/users'
import { CourseDetailed } from '@models/course'
import { useGetPositionsReportMutation } from '@services/employees'
import useMe from '@hooks/useMe'
import useEmployeesEducationsFiltersModalFields from './useEmployeesEducationsFiltersModalFields'

export enum EducationReports {
  EMPLOYEES_REPORT,
  INITIAL_BRIEFING,
  OTHER_BRIEFING,
  FIRE_SAFETY,
  ELECTRICAL_SAFETY,
  POSITIONS_REPORT_NAME,
}

type FormValues = {
  company?: Option & CompanyDetailed
  position?: Option & PositionDetailed
  division?: Option & DivisionDetailed
  filial?: Option & FilialDetailed
  conductsBriefing?: Option & User
  interval?: Option
  year?: Option
  before?: string
  after?: string
  date?: string
  course?: Option & CourseDetailed
  courseAuthor?: Option
  passedCourseFrom?: string
  passedCourseTo?: string
  courseIncomplete?: boolean
}

type FormFiltersThree = {
  courseAuthorId: string | number | undefined
  company: string | undefined
  companyBranch: string | undefined
  subdivision: string | undefined
  jobPosition: string | undefined
  'passedAt[before]': string | undefined
  'passedAt[after]': string | undefined
}

type FormFiltersFour = {
  conductsBriefing: string | undefined
  company: string | undefined
  companyBranch: string | undefined
  subdivision: string | undefined
  jobPosition: string | undefined
  passedAtStart: string | undefined
  passedAtEnd: string | undefined
}

type FormFiltersPositionsReport = {
  company: string | undefined
  companyBranch: string | undefined
  subdivision: string | undefined
  jobPosition: string | undefined
}

const useEmployeesEducationReports = (): ReportsProp => {
  const { divisionCuratorWithOneDivision } = useMe()

  const [selectedReport, setSelectedReport] = useState<
    EducationReports | undefined
  >()

  const methods = useFormProcessor()
  const filterModalFields = useEmployeesEducationsFiltersModalFields(
    selectedReport,
    methods
  )

  const courseIncomplete = methods.watch('courseIncomplete')

  const [
    getEmployeesReportMutation,
    { isLoading: getEmployeesReportMutationIsLoading },
  ] = useGetEmployeesReportMutation()

  const [
    getInitialBriefingReport,
    { isLoading: getInitialBriefingReportIsLoading },
  ] = useGetEmployeesInitialBriefingReportMutation()

  const [
    getOtherBriefingReport,
    { isLoading: getOtherBriefingReportIsLoading },
  ] = useGetEmployeesOtherBriefingReportMutation()

  const [getFireSafetyReport, { isLoading: getFireSafetyReportIsLoading }] =
    useGetFireSafetyReportMutation()

  const [
    getElectricalSafetyReport,
    { isLoading: getElectricalSafetyReportIsLoading },
  ] = useGetElectricalSafetyReportMutation()

  const [
    getPositionsReportMutation,
    { isLoading: getPositionsReportMutationIsLoading },
  ] = useGetPositionsReportMutation()

  const transformFormValues = (
    form: FormValues
  ):
    | FormFiltersThree
    | FormFiltersFour
    // eslint-disable-next-line sonarjs/cognitive-complexity
    | FormFiltersPositionsReport => {
    let before: string | undefined
    let after: string | undefined
    const interval = form.interval?.value

    if (interval) {
      switch (interval) {
        case 'year': {
          if (form.year?.value) {
            before = `31.12.${form.year.value}`
            after = `01.01.${form.year.value}`
          }

          break
        }
        case 'period': {
          if (form.before) {
            before = form.before + 'T23:59:59'
          }

          if (form.after) {
            after = form.after + 'T00:00:00'
          }

          break
        }
        case 'date': {
          if (form.date) {
            after = form.date + 'T00:00:00'
            before = form.date + 'T23:59:59'
          }

          break
        }
        // No default
      }
    }

    switch (selectedReport) {
      case EducationReports.POSITIONS_REPORT_NAME:
        return {
          company: form.company?.['@id'],
          companyBranch: form.filial?.['@id'],
          subdivision:
            form.division?.['@id'] || divisionCuratorWithOneDivision?.['@id'],
          jobPosition: form.position?.['@id'],
        } as FormFiltersPositionsReport
      case EducationReports.EMPLOYEES_REPORT:
        return {
          courseAuthorId: form.courseAuthor?.value,
          course: form.course?.['@id'],
          company: form.company?.['@id'],
          companyBranch: form.filial?.['@id'],
          subdivision:
            form.division?.['@id'] || divisionCuratorWithOneDivision?.['@id'],
          jobPosition: form.position?.['@id'],
          'passedAt[after]': form.passedCourseTo,
          'passedAt[before]': form.passedCourseFrom,
          courseIncomplete: courseIncomplete ? 'true' : undefined,
        } as FormFiltersThree
      case EducationReports.FIRE_SAFETY:
      case EducationReports.ELECTRICAL_SAFETY:
      case EducationReports.INITIAL_BRIEFING:
      case EducationReports.OTHER_BRIEFING:
      default:
        return {
          companyBranch: form.filial?.['@id'],
          conductsBriefing: form.conductsBriefing?.['@id'],
          company: form.company?.['@id'],
          subdivision:
            form.division?.['@id'] || divisionCuratorWithOneDivision?.['@id'],
          jobPosition: form.position?.['@id'],
          passedAtEnd: before,
          passedAtStart: after,
        }
    }
  }

  return {
    transformFormValues,
    filterModalFields,
    methods,
    onSelectReport: setSelectedReport,
    items: [
      {
        title: EMPLOYEES_EDUCATION_STATS_REPORT_NAME,
        isLoading: getEmployeesReportMutationIsLoading,
        onClick: getEmployeesReportMutation,
        id: EMPLOYEES_EDUCATION_STATS_REPORT,
      },
      {
        title: INITIAL_BRIEFING_REPORT_NAME,
        isLoading: getInitialBriefingReportIsLoading,
        onClick: getInitialBriefingReport,
        id: INITIAL_BRIEFING_REPORT,
      },
      {
        title: OTHER_BRIEFING_REPORT_NAME,
        isLoading: getOtherBriefingReportIsLoading,
        onClick: getOtherBriefingReport,
        id: OTHER_BRIEFING_REPORT,
      },
      {
        title: ANTI_FIRE_REPORT_NAME,
        isLoading: getFireSafetyReportIsLoading,
        onClick: getFireSafetyReport,
        id: ANTI_FIRE_REPORT,
      },
      {
        title: ELECTRO_SAFE_REPORT_NAME,
        isLoading: getElectricalSafetyReportIsLoading,
        onClick: getElectricalSafetyReport,
        id: ELECTRO_SAFE_REPORT,
      },
      {
        title: POSITIONS_REPORT_NAME,
        isLoading: getPositionsReportMutationIsLoading,
        onClick: getPositionsReportMutation,
        id: POSITIONS_REPORT,
      },
    ],
  }
}

export default useEmployeesEducationReports
